import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AppShell } from "../components/AppShell";
import { BuyCreditsDialog } from "../components/BuyCreditsDialog";
import { Button } from "../components/ui/button";
import {
  Sparkles, Crown, LogOut, Mail, User, CreditCard, Settings, HelpCircle,
  ChevronRight, Zap, Film, Palette, MessageCircle,
} from "lucide-react";

const MENU = [
  { icon: CreditCard, label: "Plan & billing", desc: "Manage subscription", to: "/pricing", testId: "menu-billing", badge: null },
  { icon: Film, label: "My gallery", desc: "Your generated videos", to: "/gallery", testId: "menu-gallery" },
  { icon: Palette, label: "Studio", desc: "Create a video", to: "/studio", testId: "menu-studio" },
  { icon: Settings, label: "Settings", desc: "Preferences (coming soon)", to: null, testId: "menu-settings" },
  { icon: HelpCircle, label: "Help center", desc: "Support & documentation", to: null, testId: "menu-help" },
];

const ADMIN_MENU = [];  // Admin now handled via Telegram bot for security

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [buyOpen, setBuyOpen] = useState(false);
  const { data: plansData } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });
  const currentPlan = (plansData?.plans || []).find((p) => p.id === (user?.plan || "free"));
  const totalCredits = currentPlan?.credits_monthly || 30;
  const usedCredits = Math.max(0, totalCredits - (user?.credits || 0));
  const pct = Math.min(100, Math.max(0, (usedCredits / Math.max(totalCredits, 1)) * 100));

  if (!user) return null;

  const initial = (user.name || user.email)[0]?.toUpperCase();

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Profile header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="flex items-center gap-4 mb-8">
          <div className="relative">
            <div data-testid="profile-avatar" className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white text-2xl font-black font-display">
              {initial}
            </div>
            {user.plan !== "free" && (
              <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-500 border-2 border-[#09090B] flex items-center justify-center">
                <Crown className="w-3 h-3 text-white" fill="currentColor" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div data-testid="profile-name" className="font-display font-extrabold text-2xl tracking-tight text-white truncate">
              {user.name || user.email.split("@")[0]}
            </div>
            <div data-testid="profile-email" className="text-sm text-zinc-400 flex items-center gap-1.5 mt-0.5">
              <Mail className="w-3.5 h-3.5" />
              <span className="truncate">{user.email}</span>
            </div>
          </div>
        </motion.div>

        {/* Credit usage */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
          onClick={() => setBuyOpen(true)}
          data-testid="credit-usage-card"
          className="cursor-pointer p-5 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.02] border border-white/10 hover:border-white/25 transition-colors mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-fuchsia-500/15 border border-fuchsia-500/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-fuchsia-400" />
              </div>
              <div>
                <div className="font-display font-bold text-white">Credit usage</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{currentPlan?.name || "Explorer"} plan</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </div>
          <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: "linear-gradient(90deg, #D946EF 0%, #06B6D4 100%)" }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className="text-zinc-400"><span className="text-white font-mono font-semibold">Spent {usedCredits}</span></span>
            <span className="text-zinc-400"><span className="text-white font-mono font-semibold">Available {user.credits}</span></span>
          </div>
        </motion.div>

        {/* Buy credits CTA */}
        <motion.button
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }}
          data-testid="profile-buy-credits"
          onClick={() => setBuyOpen(true)}
          className="w-full flex items-center gap-3 p-4 rounded-2xl mb-6 text-white text-left transition-transform duration-200 hover:brightness-110 active:scale-[0.99]"
          style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", boxShadow: "0 8px 24px -6px rgba(37,211,102,0.45)" }}
        >
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-base">Buy credits</div>
            <div className="text-xs opacity-90">Instant top-up on WhatsApp</div>
          </div>
          <ChevronRight className="w-5 h-5 opacity-80" />
        </motion.button>

        {/* Menu */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl bg-white/[0.02] border border-white/10 overflow-hidden divide-y divide-white/5 mb-6">
          {[...MENU, ...(user.is_admin ? ADMIN_MENU : [])].map((item) => (
            <button
              key={item.label}
              data-testid={item.testId}
              onClick={() => item.to ? navigate(item.to) : null}
              disabled={!item.to}
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/[0.03] transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-zinc-300" strokeWidth={2} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-medium text-white">{item.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{item.desc}</div>
              </div>
              {item.badge && (
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30">
                  {item.badge}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-zinc-500" />
            </button>
          ))}
        </motion.div>

        {/* Logout */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
          <Button
            data-testid="profile-logout"
            onClick={() => { logout(); navigate("/"); }}
            variant="outline"
            className="w-full h-12 rounded-2xl border-white/10 bg-white/[0.02] hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300 text-zinc-300 font-medium"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </motion.div>

        <div className="text-center text-[10px] uppercase tracking-widest text-zinc-600 mt-10">
          AI Bazaar · v1.0
        </div>
      </div>

      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} reason="Contact us on WhatsApp to top up your credits instantly." />
    </AppShell>
  );
}
